import { AdminApiError } from "./api_error";
import { adminOperations, type AdminOperationName } from "./generated/adminOperations";
import type { AdminAuthenticationAdapter } from "../authentication/admin_authentication";

export interface AdminControl {
  reasonCode?: string;
  reasonText?: string;
  idempotencyKey?: string;
  expectedVersion?: number;
}

export interface ApiSuccessMeta {
  requestId: string;
  correlationId: string;
  auditEventId?: string | null;
  serverTime: string;
}

export interface ApiSuccess<T> { data: T; meta: ApiSuccessMeta }

export class ApiExecutor {
  constructor(
    private readonly baseUrl: string,
    private readonly auth: AdminAuthenticationAdapter,
  ) {}

  async execute<TResponse>(
    operationName: AdminOperationName,
    operationRequest: unknown,
    adminControl: AdminControl = {},
  ): Promise<ApiSuccess<TResponse>> {
    const op = adminOperations[operationName];
    if (op.transport !== "json_http") {
      throw new Error(`${operationName} is not a JSON HTTP operation`);
    }
    const authorization = await this.auth.getAuthorization();
    const response = await fetch(`${this.baseUrl}${op.path}`, {
      method: op.method,
      credentials: authorization.credentials ?? "include",
      headers: {"Content-Type": "application/json", ...(authorization.headers ?? {})},
      body: JSON.stringify({operationRequest, adminControl}),
    });
    const payload = await response.json().catch(() => null) as any;
    if (!response.ok || !payload?.data) {
      throw new AdminApiError(
        payload?.error?.code ?? `http_${response.status}`,
        payload?.error?.messageKey ?? "admin.api.request_failed",
        Boolean(payload?.error?.retryable),
        payload?.error?.safeDetails,
      );
    }
    return payload as ApiSuccess<TResponse>;
  }
}
